import * as React from 'react';

import { Icon } from './Icons/Icon';

type WaitPhase = {
  /** Inclusive lower bound in ms since start. */
  fromMs: number;
  label: string;
  icon: 'Brain' | 'AI' | 'Pending';
};

const DEFAULT_PHASES: WaitPhase[] = [
  { fromMs: 0, label: 'Thinking…', icon: 'Brain' },
  { fromMs: 1200, label: 'Working…', icon: 'Pending' },
  { fromMs: 3500, label: 'Drafting a response…', icon: 'AI' },
  { fromMs: 10000, label: 'Finalizing…', icon: 'Pending' },
];

const getPhase = (elapsedMs: number, phases: WaitPhase[]) => {
  let current = phases[0];
  for (const phase of phases) {
    if (elapsedMs >= phase.fromMs) {
      current = phase;
    }
  }
  return current;
};

export type AssistantStatusIndicatorProps = {
  /** When false, nothing is rendered. */
  active: boolean;
  /**
   * Optional label shown before the status. Useful in compact views.
   * Example: "Assistant"
   */
  prefixLabel?: string;
  /** Visual density for narrow sidebars. */
  density?: 'normal' | 'compact';
  /** Override default phases. */
  phases?: WaitPhase[];
  className?: string;
};

export const AssistantStatusIndicator: React.FC<AssistantStatusIndicatorProps> = ({
  active,
  prefixLabel,
  density = 'normal',
  phases = DEFAULT_PHASES,
  className,
}) => {
  const [startMs, setStartMs] = React.useState<number | null>(null);
  const [nowMs, setNowMs] = React.useState<number>(() => Date.now());

  React.useEffect(() => {
    if (!active) {
      setStartMs(null);
      return;
    }
    setStartMs(Date.now());
  }, [active]);

  React.useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [active]);

  if (!active) return null;

  const elapsed = startMs ? Math.max(0, nowMs - startMs) : 0;
  const phase = getPhase(elapsed, phases);

  const isCompact = density === 'compact';
  const iconSize = isCompact ? 14 : 16;
  const textClass = isCompact ? 'text-[11px]' : 'text-xs';

  return (
    <div
      className={[
        'flex items-center gap-2 text-neutral-500',
        textClass,
        className ?? '',
      ].join(' ')}
      role="status"
      aria-live="polite"
    >
      <span
        className={[
          'inline-flex items-center justify-center rounded-full border border-neutral-200 bg-white',
          isCompact ? 'h-6 w-6' : 'h-7 w-7',
        ].join(' ')}
        aria-hidden="true"
      >
        <Icon name={phase.icon} size={iconSize} className="text-neutral-500" />
      </span>

      {prefixLabel ? <span className="font-medium text-neutral-400">{prefixLabel}</span> : null}
      <span className="font-medium text-neutral-600">{phase.label}</span>
    </div>
  );
};

export default AssistantStatusIndicator;

